import {
    FinancialAccountSourceType,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { AccountRole } from "@common/types/domains/accounting/account";

export enum ChartOfAccountsTemplateVariableKind {
    bankAccountChecking = "bankAccountChecking",
    bankAccountSavings = "bankAccountSavings",
    investment = "investment",
    paymentProcessor = "paymentProcessor",
    creditCard = "creditCard",
    loan = "loan",
    reimbursementsPayable = "reimbursementsPayable",
}

export const MAX_CHART_OF_ACCOUNTS_TEMPLATE_GROUPS = 500;
export const MAX_CHART_OF_ACCOUNTS_TEMPLATE_ACCOUNTS = 1000;

export enum ChartOfAccountsTemplateUploadSource {
    quickBooksOnline = "quickBooksOnline",
    quickBooksDesktop = "quickBooksDesktop",
    xero = "xero",
    kickTemplate = "kickTemplate",
}

// Shown on every (disabled) action of a variable account row in the template
// editor: variables are fixed integration placeholders.
export const VARIABLE_ACCOUNT_ACTION_TOOLTIP =
    "Integration placeholder for financial account you connect to kick; " +
    "cannot be edited, archived, or deleted.";

export const REQUIRED_KICK_ACCOUNT_ROLES = [
    AccountRole.cashOnHand,
    AccountRole.bankTransferClearing,
    AccountRole.paymentTransferClearing,
    AccountRole.accountsReceivable,
    AccountRole.intercompanyReceivable,
    AccountRole.accountsPayable,
    AccountRole.creditCardClearing,
    AccountRole.intercompanyPayable,
    AccountRole.uncategorizedIncome,
    AccountRole.payrollClearing,
    AccountRole.uncategorizedExpense,
] satisfies readonly AccountRole[];

export interface ChartOfAccountsTemplateVariableMatchRule {
    financialTypes: FinancialAccountType[];
    sourceTypes: FinancialAccountSourceType[];
    nameEndsWith: string | null;
}

export const CHART_OF_ACCOUNTS_TEMPLATE_VARIABLE_MATCH_RULES: Record<
    ChartOfAccountsTemplateVariableKind,
    ChartOfAccountsTemplateVariableMatchRule
> = {
    [ChartOfAccountsTemplateVariableKind.bankAccountChecking]: {
        financialTypes: [FinancialAccountType.CHECKING],
        sourceTypes: [
            FinancialAccountSourceType.PLAID,
            FinancialAccountSourceType.MERCURY,
        ],
        nameEndsWith: null,
    },
    [ChartOfAccountsTemplateVariableKind.bankAccountSavings]: {
        financialTypes: [FinancialAccountType.SAVINGS],
        sourceTypes: [
            FinancialAccountSourceType.PLAID,
            FinancialAccountSourceType.MERCURY,
        ],
        nameEndsWith: null,
    },
    [ChartOfAccountsTemplateVariableKind.investment]: {
        financialTypes: [FinancialAccountType.INVESTMENT],
        sourceTypes: [
            FinancialAccountSourceType.PLAID,
            FinancialAccountSourceType.MERCURY,
        ],
        nameEndsWith: null,
    },
    [ChartOfAccountsTemplateVariableKind.paymentProcessor]: {
        financialTypes: [FinancialAccountType.PAYMENT_PROCESSOR],
        sourceTypes: [
            FinancialAccountSourceType.STRIPE,
            FinancialAccountSourceType.PAYPAL,
            FinancialAccountSourceType.PLAID,
        ],
        nameEndsWith: null,
    },
    [ChartOfAccountsTemplateVariableKind.creditCard]: {
        financialTypes: [FinancialAccountType.CREDIT_CARD],
        sourceTypes: [
            FinancialAccountSourceType.PLAID,
            FinancialAccountSourceType.MERCURY,
            FinancialAccountSourceType.RAMP,
        ],
        nameEndsWith: null,
    },
    [ChartOfAccountsTemplateVariableKind.loan]: {
        financialTypes: [
            FinancialAccountType.LOAN,
            FinancialAccountType.LINE_OF_CREDIT,
        ],
        sourceTypes: [
            FinancialAccountSourceType.PLAID,
            FinancialAccountSourceType.MERCURY,
        ],
        nameEndsWith: null,
    },
    [ChartOfAccountsTemplateVariableKind.reimbursementsPayable]: {
        financialTypes: [],
        sourceTypes: [FinancialAccountSourceType.RAMP],
        nameEndsWith: "- Reimbursements",
    },
};

// Resolves the variable placeholder a connected financial account maps to.
// Name-specific rules are checked first so a RAMP "...- Reimbursements" credit
// card routes to reimbursementsPayable instead of the generic creditCard rule.
export const resolveTemplateVariableKindForFinancialAccount = (params: {
    financialType: FinancialAccountType;
    sourceType: FinancialAccountSourceType;
    name: string | null;
}): ChartOfAccountsTemplateVariableKind | null => {
    const kinds = Object.values(ChartOfAccountsTemplateVariableKind);

    // Shared by both passes: the account's source must be allowed and either the
    // rule places no financial-type constraint or the account's type is listed.
    const isSourceAndFinancialTypeMatch = (
        rule: ChartOfAccountsTemplateVariableMatchRule,
    ): boolean =>
        rule.sourceTypes.includes(params.sourceType) &&
        (rule.financialTypes.length === 0 ||
            rule.financialTypes.includes(params.financialType));

    // Pass 1 (name-specific rules): must run first so a RAMP
    // "...- Reimbursements" credit card routes to reimbursementsPayable rather
    // than the generic credit-card rule.
    for (const kind of kinds) {
        const rule = CHART_OF_ACCOUNTS_TEMPLATE_VARIABLE_MATCH_RULES[kind];
        if (rule.nameEndsWith === null) {
            continue;
        }
        const hasNameMatch =
            params.name !== null && params.name.endsWith(rule.nameEndsWith);
        if (isSourceAndFinancialTypeMatch(rule) && hasNameMatch) {
            return kind;
        }
    }

    // Pass 2 (type rules): only rules with a non-empty financialTypes list
    // match here; the name-only RAMP rule has empty financialTypes and is
    // handled above.
    for (const kind of kinds) {
        const rule = CHART_OF_ACCOUNTS_TEMPLATE_VARIABLE_MATCH_RULES[kind];
        if (rule.nameEndsWith !== null) {
            continue;
        }
        if (
            rule.financialTypes.length > 0 &&
            isSourceAndFinancialTypeMatch(rule)
        ) {
            return kind;
        }
    }

    return null;
};
